import { ColumnDef } from '@tanstack/react-table';
import { getAllVendors } from '@/services/Network';
import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { VendorDetails } from './VendorDetails';
import VendorDocumentDialog from './VendorDocumentDialog';
import { Badge } from '@/components/ui/badge';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { Download, Edit3 } from 'lucide-react';
import { ExportVendorDialog } from './ExportVendorDialog';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import { VendorStatus, type VendorResponse } from '@/types/vendor.types';

export const Vendors = () => {
  const navigate = useNavigate();
  const [, setSearchParam] = useSearchParams();
  const [openVendorDetails, setOpenVendorDetails] = useState(false);
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [openDocumentList, setOpenDocumentList] = useState<boolean>(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const columns: ColumnDef<VendorResponse>[] = [
    {
      accessorKey: 'name',
      header: 'Vendor Name',
      enableSorting: true,
      size: 180,
      cell: ({ cell }) => {
        return (
          <div className="w-full h-full cursor-pointer min-w-[170px] flex flex-col">
            <OneLineText className="mr-5 min-w-[150px] font-medium" text={cell.row.original.name} />
            {['INACTIVE', 'ACTIVE'].includes(cell.row.original.status) && (
              <span className="text-xs text-gray-500">{`Created on: ${convertISODateToMMDDYYYY(cell.row.original.createdAt)} ${convertISOToTime(cell.row.original.createdAt)}`}</span>
            )}
            {cell.row.original.status === VendorStatus.INACTIVE && (
              <span className="text-xs text-error">{`Deactivated on: ${convertISODateToMMDDYYYY(cell.row.original.deactivatedAt)} ${convertISOToTime(cell.row.original.deactivatedAt)}`}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.address1} />;
      },
    },
    {
      accessorKey: 'city',
      header: 'City',
      enableSorting: true,
      size: 120,
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.city ?? '-'} />;
      },
    },
    {
      accessorKey: 'state',
      header: 'State',
      enableSorting: true,
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.state ?? '-'} />;
      },
    },
    {
      accessorKey: 'zipCode',
      header: 'Zip Code',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.zipCode ?? '-'} />;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[200px]" text={row.original.email ?? '-'} />;
      },
    },
    {
      accessorKey: 'taxId',
      header: 'Tax ID',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px] text-sm" text={row.original.taxId ?? '-'} />;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ cell }) => {
        const status = cell.row.original.status;
        if (status === VendorStatus.PENDING) {
          return <Badge className={`bg-[#ffeebf] text-[#896010] hover:bg-[#ffeebf] mr-6`}>Pending</Badge>;
        }
        if (status === VendorStatus.INACTIVE) {
          return <Badge className={`bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5] mr-6`}>Inactive</Badge>;
        }
        if (status === VendorStatus.ACTIVE) {
          return <Badge className={`bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3] mr-6`}>Active</Badge>;
        }
      },
    },
    {
      accessorKey: 'documents',
      header: 'Documents',
      cell: ({ row }) => {
        return (
          <div
            className="text-primary mr-5 min-w-[100px] cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setOpenDocumentList(true);
              setVendorId(row.original.vendorId);
            }}
          >
            View List
          </div>
        );
      },
    },
    {
      accessorKey: 'update',
      header: 'Action',
      cell: ({ row }) => {
        return (
          <Button
            className="text-primary mr-5 min-w-[100px] cursor-pointer bg-transparent flex justify-start border-none p-0 h-fit hover:bg-transparent"
            aria-label="Edit Vendor"
            onClick={(e) => {
              e.stopPropagation();
              setSearchParam({ vendorId: row.original.vendorId });
              setOpenVendorDetails(true);
              setVendorId(row.original.vendorId);
              navigate(`/vendors/update/${row.original.vendorId}`);
            }}
          >
            <Edit3 className="h-4 w-4 cursor-pointer" />
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <ToolBar
        title="Vendors"
        buttonTitle="Add Vendor"
        createButton={true}
        additionalButton={
          <Button
            variant="outline"
            className="bg-[#FFFFFF] text-primary w-[84px] h-[36px] border-primary hover:bg-[#EDF5FF] p-3"
            onClick={() => setIsExportDialogOpen(true)}
            data-testid="export-vendor-button"
          >
            <div className="flex flex-row items-center gap-2">
              <Download className="h-4 w-4" />
              <span className="font-medium hidden sm:block">Export</span>
            </div>
          </Button>
        }
        handleClick={() => navigate('/vendors/create')}
      />
      <DataTable
        columns={columns}
        apiGet={getAllVendors}
        onRowClick={(row) => {
          setSearchParam({ vendorId: row.vendorId });
          setOpenVendorDetails(true);
          setVendorId(row.vendorId);
        }}
        search={[
          {
            label: 'Search Vendors by Name',
            name: 'name',
            placeholder: 'Search Vendors by Name ',
          },
        ]}
      />
      {openVendorDetails && vendorId && (
        <VendorDetails
          onCloseClick={() => {
            setOpenVendorDetails(false);
            setSearchParam({});
          }}
          vendorId={vendorId}
        />
      )}
      {openDocumentList && vendorId && (
        <VendorDocumentDialog vendorId={vendorId} onCloseClick={() => setOpenDocumentList(false)} />
      )}
      {isExportDialogOpen && <ExportVendorDialog onClose={() => setIsExportDialogOpen(false)} />}
    </>
  );
};
